import { Injectable, signal, effect } from '@angular/core';

export interface ThemePreset {
  id: string;
  name: string;
  primaryColor: string;
  hoverColor: string;
  badgeBg: string;
  badgeText: string;
}

export interface ColorSchemePreset {
  id: string;
  name: string;
  screenBg: string;
  cardBg: string;
  labelColor: string;
}

export interface OrganizationSettings {
  cardRadius: number;
  inputRadius: number;
  selectRadius: number;
  buttonRadius: number;
  tableRadius: number;
  defaultPageSize: number;
  activeThemeId: string;
  customPrimaryColor?: string;
  screenBgColor?: string;
  cardBgColor?: string;
  labelTextColor?: string;
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationSettingsService {
  private readonly STORAGE_KEY = 'org_central_settings';

  readonly themePresets: ThemePreset[] = [
    {
      id: 'teal',
      name: 'Teal (Default)',
      primaryColor: '#0f766e',
      hoverColor: '#0d6861',
      badgeBg: '#ccfbf1',
      badgeText: '#115e59'
    },
    {
      id: 'ocean',
      name: 'Ocean Blue',
      primaryColor: '#0284c7',
      hoverColor: '#0369a1',
      badgeBg: '#e0f2fe',
      badgeText: '#0369a1'
    },
    {
      id: 'emerald',
      name: 'Emerald Green',
      primaryColor: '#10b981',
      hoverColor: '#059669',
      badgeBg: '#d1fae5',
      badgeText: '#047857'
    },
    {
      id: 'indigo',
      name: 'Royal Indigo',
      primaryColor: '#6366f1',
      hoverColor: '#4f46e5',
      badgeBg: '#e0e7ff',
      badgeText: '#4338ca'
    },
    {
      id: 'purple',
      name: 'Modern Purple',
      primaryColor: '#8b5cf6',
      hoverColor: '#7c3aed',
      badgeBg: '#ede9fe',
      badgeText: '#6d28d9'
    },
    {
      id: 'slate',
      name: 'Dark Slate',
      primaryColor: '#0f172a',
      hoverColor: '#1e293b',
      badgeBg: '#f1f5f9',
      badgeText: '#0f172a'
    }
  ];

  readonly colorSchemePresets: ColorSchemePreset[] = [
    {
      id: 'slate_light',
      name: 'Clean Slate (Default)',
      screenBg: '#f8fafc',
      cardBg: '#ffffff',
      labelColor: '#64748b'
    },
    {
      id: 'soft_gray',
      name: 'Soft Gray',
      screenBg: '#f3f4f6',
      cardBg: '#ffffff',
      labelColor: '#475569'
    },
    {
      id: 'warm_cream',
      name: 'Warm Cream',
      screenBg: '#fafaf9',
      cardBg: '#ffffff',
      labelColor: '#57534e'
    },
    {
      id: 'cool_ice',
      name: 'Cool Ice Tint',
      screenBg: '#f0f9ff',
      cardBg: '#ffffff',
      labelColor: '#0369a1'
    },
    {
      id: 'midnight_dark',
      name: 'Midnight Dark',
      screenBg: '#0f172a',
      cardBg: '#1e293b',
      labelColor: '#94a3b8'
    }
  ];

  // System default values
  readonly DEFAULT_SETTINGS: OrganizationSettings = {
    cardRadius: 10,
    inputRadius: 6,
    selectRadius: 6,
    buttonRadius: 6,
    tableRadius: 10,
    defaultPageSize: 10,
    activeThemeId: 'teal',
    screenBgColor: '#f8fafc',
    cardBgColor: '#ffffff',
    labelTextColor: '#64748b'
  };

  // Angular Signals for reactive state
  cardRadius = signal<number>(this.DEFAULT_SETTINGS.cardRadius);
  inputRadius = signal<number>(this.DEFAULT_SETTINGS.inputRadius);
  selectRadius = signal<number>(this.DEFAULT_SETTINGS.selectRadius);
  buttonRadius = signal<number>(this.DEFAULT_SETTINGS.buttonRadius);
  tableRadius = signal<number>(this.DEFAULT_SETTINGS.tableRadius);
  defaultPageSize = signal<number>(this.DEFAULT_SETTINGS.defaultPageSize);
  activeThemeId = signal<string>(this.DEFAULT_SETTINGS.activeThemeId);
  customPrimaryColor = signal<string | undefined>(undefined);
  screenBgColor = signal<string>(this.DEFAULT_SETTINGS.screenBgColor || '#f8fafc');
  cardBgColor = signal<string>(this.DEFAULT_SETTINGS.cardBgColor || '#ffffff');
  labelTextColor = signal<string>(this.DEFAULT_SETTINGS.labelTextColor || '#64748b');

  constructor() {
    this.loadSettings();

    // Re-apply DOM styles whenever signals change
    effect(() => {
      this.applySettingsToDOM();
    });
  }

  /**
   * Load saved settings from localStorage
   */
  loadSettings(): void {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        const parsed: OrganizationSettings = JSON.parse(saved);
        if (typeof parsed.cardRadius === 'number') {
          this.cardRadius.set(parsed.cardRadius);
        }
        if (typeof parsed.inputRadius === 'number') {
          this.inputRadius.set(parsed.inputRadius);
        } else if (typeof parsed.cardRadius === 'number') {
          this.inputRadius.set(parsed.cardRadius);
        }
        if (typeof parsed.selectRadius === 'number') {
          this.selectRadius.set(parsed.selectRadius);
        } else if (typeof parsed.cardRadius === 'number') {
          this.selectRadius.set(parsed.cardRadius);
        }
        if (typeof parsed.buttonRadius === 'number') {
          this.buttonRadius.set(parsed.buttonRadius);
        } else if (typeof parsed.cardRadius === 'number') {
          this.buttonRadius.set(parsed.cardRadius);
        }
        if (typeof parsed.tableRadius === 'number') {
          this.tableRadius.set(parsed.tableRadius);
        } else if (typeof parsed.cardRadius === 'number') {
          this.tableRadius.set(parsed.cardRadius);
        }
        if (typeof parsed.defaultPageSize === 'number') {
          this.defaultPageSize.set(parsed.defaultPageSize);
        }
        if (parsed.activeThemeId) {
          this.activeThemeId.set(parsed.activeThemeId);
        }
        if (parsed.customPrimaryColor) {
          this.customPrimaryColor.set(parsed.customPrimaryColor);
        }
        if (parsed.screenBgColor) {
          this.screenBgColor.set(parsed.screenBgColor);
        }
        if (parsed.cardBgColor) {
          this.cardBgColor.set(parsed.cardBgColor);
        }
        if (parsed.labelTextColor) {
          this.labelTextColor.set(parsed.labelTextColor);
        }
      }
    } catch (e) {
      console.warn('Could not load organization settings from localStorage:', e);
    }
    this.applySettingsToDOM();
  }

  /**
   * Save current settings to localStorage
   */
  saveSettings(): void {
    try {
      const settings: OrganizationSettings = {
        cardRadius: this.cardRadius(),
        inputRadius: this.inputRadius(),
        selectRadius: this.selectRadius(),
        buttonRadius: this.buttonRadius(),
        tableRadius: this.tableRadius(),
        defaultPageSize: this.defaultPageSize(),
        activeThemeId: this.activeThemeId(),
        customPrimaryColor: this.customPrimaryColor(),
        screenBgColor: this.screenBgColor(),
        cardBgColor: this.cardBgColor(),
        labelTextColor: this.labelTextColor()
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.warn('Could not save organization settings to localStorage:', e);
    }
  }

  /**
   * Set card border radius in pixels
   */
  setCardRadius(px: number): void {
    const validPx = Math.max(0, Math.min(30, px));
    this.cardRadius.set(validPx);
    this.saveSettings();
    this.applySettingsToDOM();
  }

  /**
   * Set input and select border radius in pixels
   */
  setInputRadius(px: number): void {
    const validPx = Math.max(0, Math.min(30, px));
    this.inputRadius.set(validPx);
    this.selectRadius.set(validPx);
    this.saveSettings();
    this.applySettingsToDOM();
  }

  /**
   * Set button border radius in pixels
   */
  setButtonRadius(px: number): void {
    const validPx = Math.max(0, Math.min(30, px));
    this.buttonRadius.set(validPx);
    this.saveSettings();
    this.applySettingsToDOM();
  }

  /**
   * Set table border radius in pixels
   */
  setTableRadius(px: number): void {
    const validPx = Math.max(0, Math.min(30, px));
    this.tableRadius.set(validPx);
    this.saveSettings();
    this.applySettingsToDOM();
  }

  setScreenBgColor(color: string): void {
    this.screenBgColor.set(color);
    this.saveSettings();
    this.applySettingsToDOM();
  }

  setCardBgColor(color: string): void {
    this.cardBgColor.set(color);
    this.saveSettings();
    this.applySettingsToDOM();
  }

  setLabelTextColor(color: string): void {
    this.labelTextColor.set(color);
    this.saveSettings();
    this.applySettingsToDOM();
  }

  setColorScheme(screenBg: string, cardBg: string, labelColor: string): void {
    this.screenBgColor.set(screenBg);
    this.cardBgColor.set(cardBg);
    this.labelTextColor.set(labelColor);
    this.saveSettings();
    this.applySettingsToDOM();
  }

  /**
   * Set global default page size
   */
  setPageSize(size: number): void {
    this.defaultPageSize.set(size);
    this.saveSettings();
  }

  /**
   * Set active theme preset
   */
  setTheme(themeId: string, customColor?: string): void {
    this.activeThemeId.set(themeId);
    if (customColor) {
      this.customPrimaryColor.set(customColor);
    } else {
      this.customPrimaryColor.set(undefined);
    }
    this.saveSettings();
    this.applySettingsToDOM();
  }

  /**
   * Reset all settings to system defaults
   */
  resetToDefaults(): void {
    this.cardRadius.set(this.DEFAULT_SETTINGS.cardRadius);
    this.inputRadius.set(this.DEFAULT_SETTINGS.inputRadius);
    this.selectRadius.set(this.DEFAULT_SETTINGS.selectRadius);
    this.buttonRadius.set(this.DEFAULT_SETTINGS.buttonRadius);
    this.tableRadius.set(this.DEFAULT_SETTINGS.tableRadius);
    this.defaultPageSize.set(this.DEFAULT_SETTINGS.defaultPageSize);
    this.activeThemeId.set(this.DEFAULT_SETTINGS.activeThemeId);
    this.customPrimaryColor.set(undefined);
    this.screenBgColor.set(this.DEFAULT_SETTINGS.screenBgColor || '#f8fafc');
    this.cardBgColor.set(this.DEFAULT_SETTINGS.cardBgColor || '#ffffff');
    this.labelTextColor.set(this.DEFAULT_SETTINGS.labelTextColor || '#64748b');
    localStorage.removeItem(this.STORAGE_KEY);
    this.applySettingsToDOM();
  }

  /**
   * Get active theme preset or custom details
   */
  get currentTheme(): ThemePreset {
    const preset = this.themePresets.find(t => t.id === this.activeThemeId()) || this.themePresets[0];
    const customHex = this.customPrimaryColor();
    if (customHex) {
      return {
        id: 'custom',
        name: 'Custom Theme',
        primaryColor: customHex,
        hoverColor: customHex,
        badgeBg: '#f3f4f6',
        badgeText: customHex
      };
    }
    return preset;
  }

  /**
   * Apply CSS Custom Properties to document root (:root)
   */
  applySettingsToDOM(): void {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    const theme = this.currentTheme;

    root.style.setProperty('--card-border-radius', `${this.cardRadius()}px`);
    root.style.setProperty('--input-border-radius', `${this.inputRadius()}px`);
    root.style.setProperty('--select-border-radius', `${this.selectRadius()}px`);
    root.style.setProperty('--button-border-radius', `${this.buttonRadius()}px`);
    root.style.setProperty('--table-border-radius', `${this.tableRadius()}px`);
    root.style.setProperty('--theme-primary', theme.primaryColor);
    root.style.setProperty('--theme-primary-hover', theme.hoverColor);
    root.style.setProperty('--screen-bg', this.screenBgColor());
    root.style.setProperty('--card-bg', this.cardBgColor());
    root.style.setProperty('--label-color', this.labelTextColor());
  }
}
